import { useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";
import { fmt, fmtDuration, short } from "../format";
import { RefreshButton } from "./RefreshButton";
import type { FleetRun, Scraper } from "../types";

export type RunFilter = "all" | "failed" | "success" | "running";
const FILTERS: RunFilter[] = ["all", "failed", "success", "running"];

/** Seconds between two ISO timestamps, or null if the run hasn't finished. */
function durationSec(startedAt: string, finishedAt: string | null): number | null {
  if (!finishedAt) return null;
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  return ms >= 0 ? Math.round(ms / 1000) : null;
}

/**
 * Fleet-wide run history — operator-only, spans every tenant (GET /runs). The "failed → runs" drill
 * from Overview lands here pre-filtered. Kintsugi: a clean run is plain ink; only a failure gets an
 * iron-oxide mark.
 */
const PER_PAGE_OPTIONS = [25, 50, 100, 200];

export function Runs({ initialStatus = "all" }: { initialStatus?: RunFilter }) {
  const [filter, setFilterState] = useState<RunFilter>(initialStatus);
  const [perPage, setPerPageState] = useState(50);
  const [page, setPage] = useState(0); // 0-based

  // Changing the filter or the page size resets back to the first page.
  const setFilter = (f: RunFilter) => {
    setFilterState(f);
    setPage(0);
  };
  const setPerPage = (n: number) => {
    setPerPageState(n);
    setPage(0);
  };

  const qs = new URLSearchParams({ limit: String(perPage), offset: String(page * perPage) });
  if (filter !== "all") qs.set("status", filter);
  const { data, loading, error, reload } = useAsync<FleetRun[]>(
    () => api(`/runs?${qs.toString()}`),
    [filter, perPage, page],
  );
  // Names: /scrapers spans every tenant for an operator; /tenants gives the owning client.
  const { data: scrapers } = useAsync<Scraper[]>(() => api("/scrapers"), []);
  // /tenants is paginated: `{ clients: [...] }`, NOT `{ tenants: [...] }`. (Reading the wrong key
  // crashed `.find` once the fetch resolved.)
  const { data: tenantPage } = useAsync<{ clients: { id: string; name: string }[] }>(
    () => api("/tenants?pageSize=200"),
    [],
  );
  const scraperName = (id: string) => scrapers?.find((s) => s.id === id)?.name ?? short(id);
  const clientName = (id: string) => tenantPage?.clients.find((t) => t.id === id)?.name ?? short(id);

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  const runs = data ?? [];

  return (
    <div className="stack">
      <div className="section-head">
        <h1>Runs</h1>
        <div className="row-gap">
          <RefreshButton onClick={reload} />
        </div>
      </div>

      <div className="row-gap">
        <label className="filter-select muted sm">
          Status
          <select value={filter} onChange={(e) => setFilter(e.target.value as RunFilter)}>
            {FILTERS.map((f) => (
              <option key={f} value={f}>
                {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-select muted sm">
          Per page
          <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
            {PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      {runs.length === 0 && page === 0 && (
        <div className="empty">
          {filter === "all" ? "No runs yet." : `No ${filter} runs.`}
        </div>
      )}

      {runs.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Scraper</th>
                <th>Status</th>
                <th className="num">Items</th>
                <th>Failure</th>
                <th>Started</th>
                <th className="num">Took</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const secs = durationSec(r.started_at, r.finished_at);
                return (
                  <tr key={r.id} className={r.status === "failed" ? "is-fail" : undefined}>
                    <td>{clientName(r.tenant_id)}</td>
                    <td>{scraperName(r.scraper_id)}</td>
                    <td>
                      <span className={`pill ${r.status}`}>{r.status}</span>
                    </td>
                    <td className="num">{r.item_count ?? "—"}</td>
                    <td className="muted">{r.failure_class ?? "—"}</td>
                    <td className="nowrap">{fmt(r.started_at)}</td>
                    <td className="num">{secs === null ? "—" : fmtDuration(secs)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(page > 0 || runs.length === perPage) && (
        <div className="row-gap" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <span className="muted sm">
            {runs.length > 0
              ? `Showing ${page * perPage + 1}–${page * perPage + runs.length}`
              : "No more runs"}
            {" · page "}
            {page + 1}
          </span>
          <div className="row-gap">
            <button
              className="btn ghost sm"
              disabled={page === 0 || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ← Prev
            </button>
            <button
              className="btn ghost sm"
              disabled={runs.length < perPage || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
